(() => {
  // Source-faithful custom-view shell: real links stay normal navigation; only menus and policy disclosures toggle locally.
  document.querySelectorAll('[data-heir-shell]').forEach((shell) => {
    const menuButton = shell.querySelector('[data-heir-menu-toggle]');
    const menu = menuButton ? document.getElementById(menuButton.getAttribute('aria-controls')) : null;
    const closeMenu = (returnFocus) => {
      if (!menuButton || !menu) return;
      menu.hidden = true;
      menuButton.setAttribute('aria-expanded', 'false');
      menuButton.setAttribute('aria-label', 'Open navigation');
      if (returnFocus) menuButton.focus();
    };
    menuButton?.addEventListener('click', () => {
      const nextOpen = menu?.hidden;
      if (!menu || nextOpen === undefined) return;
      menu.hidden = !nextOpen;
      menuButton.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');
      menuButton.setAttribute('aria-label', nextOpen ? 'Close navigation' : 'Open navigation');
    });
    shell.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && menu && !menu.hidden) closeMenu(true);
    });
  });

  document.querySelectorAll('[data-heir-shell-footer]').forEach((footer) => {
    const buttons = [...footer.querySelectorAll('[data-heir-policy]')];
    const panels = [...footer.querySelectorAll('[data-heir-policy-panel]')];
    buttons.forEach((button) => button.addEventListener('click', () => {
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      const shouldOpen = button.getAttribute('aria-expanded') !== 'true';
      buttons.forEach((control) => control.setAttribute('aria-expanded', 'false'));
      panels.forEach((item) => { item.hidden = true; });
      if (panel && shouldOpen) {
        panel.hidden = false;
        button.setAttribute('aria-expanded', 'true');
      }
    }));
  });

  // Homepage controls port the source's manual carousel and autoplaying silent film behavior.
  const home = document.querySelector('[data-heir-home]');
  if (home) {
    const slides = [...home.querySelectorAll('[data-heir-slide]')];
    const dots = [...home.querySelectorAll('[data-heir-goto]')];
    const count = home.querySelector('[data-heir-slide-count]');
    let current = 0;
    const show = (index) => {
      current = (index + slides.length) % slides.length;
      slides.forEach((slide, i) => { slide.hidden = i !== current; slide.classList.toggle('is-active', i === current); });
      dots.forEach((dot, i) => { dot.classList.toggle('is-active', i === current); dot.setAttribute('aria-current', i === current ? 'true' : 'false'); });
      if (count) count.textContent = `${String(current + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`;
    };
    home.querySelector('[data-heir-previous]')?.addEventListener('click', () => show(current - 1));
    home.querySelector('[data-heir-next]')?.addEventListener('click', () => show(current + 1));
    dots.forEach((dot) => dot.addEventListener('click', () => show(Number(dot.dataset.heirGoto))));
    const video = home.querySelector('[data-heir-video]');
    const toggle = home.querySelector('[data-heir-video-toggle]');
    const syncVideoToggle = () => {
      if (!video || !toggle) return;
      const playing = !video.paused && !video.ended;
      toggle.textContent = playing ? 'Pause film Ⅱ' : 'Play film ▷';
      toggle.setAttribute('aria-label', playing ? 'Pause background film' : 'Play background film');
    };
    video?.addEventListener('play', syncVideoToggle);
    video?.addEventListener('pause', syncVideoToggle);
    video?.addEventListener('ended', syncVideoToggle);
    syncVideoToggle();
    toggle?.addEventListener('click', async () => {
      if (!video) return;
      if (video.paused) { try { await video.play(); } catch (_) {} }
      else { video.pause(); }
      syncVideoToggle();
    });
    const guidance = home.querySelector('.heir-html__guidance details');
    guidance?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && guidance.open) {
        guidance.open = false;
        guidance.querySelector('summary')?.focus();
      }
    });
    const guidanceForm = home.querySelector('#heir-guidance-form');
    const resetGuidanceSubmit = () => {
      const submit = guidanceForm?.querySelector('[type="submit"]');
      if (submit) {
        submit.disabled = false;
        submit.textContent = 'Prepare my request';
      }
    };
    resetGuidanceSubmit();
    window.addEventListener('pageshow', resetGuidanceSubmit);
    guidanceForm?.addEventListener('submit', (event) => {
      const trap = guidanceForm.querySelector('.heir-html__honeypot');
      const status = guidanceForm.querySelector('[data-heir-guidance-status]');
      if (trap && trap.value.trim()) {
        event.preventDefault();
        if (status) status.textContent = 'Unable to send this request.';
        return;
      }
      const submit = guidanceForm.querySelector('[type="submit"]');
      if (submit) {
        submit.disabled = true;
        submit.textContent = 'Sending request…';
      }
      if (status) status.textContent = 'Sending your request to the Heirlooms team.';
    });
  }

  // Journal filters only change the visible source-derived cards; they never modify Shopify articles or tags.
  document.querySelectorAll('[data-heir-journal]').forEach((journal) => {
    const cards = [...journal.querySelectorAll('[data-heir-article]')];
    const subfilters = journal.querySelector('[data-heir-subfilters]');
    let category = 'All';
    let subcategory = 'All';
    const render = () => {
      cards.forEach((card) => {
        const categoryMatch = category === 'All' || card.dataset.heirCategory === category;
        const subcategoryMatch = subcategory === 'All' || card.dataset.heirSubcategory === subcategory;
        card.hidden = !(categoryMatch && subcategoryMatch);
      });
      journal.querySelectorAll('[data-heir-filter]').forEach((button) => button.setAttribute('aria-pressed', button.dataset.heirFilter === category ? 'true' : 'false'));
      const count = cards.filter((card) => !card.hidden).length;
      const status = journal.querySelector('[data-heir-count]');
      if (status) status.textContent = `${count} ${count === 1 ? 'story' : 'stories'}${category !== 'All' ? ` · ${category}` : ''}${subcategory !== 'All' ? ` / ${subcategory}` : ''}`;
    };
    const setCategory = (nextCategory) => {
      category = nextCategory;
      subcategory = 'All';
      if (subfilters) {
        subfilters.replaceChildren();
        subfilters.hidden = category === 'All';
        if (category !== 'All') {
          const values = [...new Set(cards.filter((card) => card.dataset.heirCategory === category).map((card) => card.dataset.heirSubcategory))];
          ['All', ...values].forEach((value) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.textContent = value === 'All' ? `All in ${category}` : value;
            button.setAttribute('aria-pressed', value === 'All' ? 'true' : 'false');
            button.addEventListener('click', () => {
              subcategory = value;
              [...subfilters.children].forEach((child) => child.setAttribute('aria-pressed', child === button ? 'true' : 'false'));
              render();
            });
            subfilters.appendChild(button);
          });
        }
      }
      render();
    };
    journal.querySelectorAll('[data-heir-filter]').forEach((button) => button.addEventListener('click', () => setCategory(button.dataset.heirFilter)));
    render();
  });
})();
